import React from "react";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";

import PageHeader from "../Common/PageHeader";
import ProfileForm from "./ProfileForm";
import { buildBreadcrumbs } from "helper/breadcrumbs";
import type { UserCustomer, UserData } from "../../types/state";
import { withAuthData, type AuthInjectedProps } from "../../context/AuthContext";

const ProfileFormAny = ProfileForm as unknown as React.ComponentType<Record<string, unknown>>;

interface ProfilePageOwnProps extends WithTranslation {}

interface ProfilePageInjectedProps {
  userData: UserData;
  customerData: UserCustomer | Record<string, never>;
  adminToken: string | boolean | null;
}

type ProfilePageProps = ProfilePageOwnProps & AuthInjectedProps & ProfilePageInjectedProps;

function ProfilePage({
  t,
  actions,
  userData,
  customerData,
  adminToken,
  profilePageMessage,
}: ProfilePageProps) {
  const isDelegateUser = Boolean(userData.parent_user);
  const [formData, setFormData] = React.useState<Record<string, string>>({
    first_name: userData.first_name || "",
    last_name: userData.last_name || "",
  });
  const [formError, setFormError] = React.useState<
    Record<string, { code: string; message: string }>
  >({});

  const onFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const field = event.target.name;
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: Record<string, { code: string; message: string }> = {};
    const firstName = (formData.first_name || "").trim();
    const lastName = (formData.last_name || "").trim();

    if (!isDelegateUser) {
      return;
    }
    if (lastName === "") {
      errors.last_name = {
        code: "REQUIRED",
        message: t("profile.profil.error_name_required"),
      };
    }
    if (firstName === "") {
      errors.first_name = {
        code: "REQUIRED",
        message: t("profile.profil.error_firstname_required"),
      };
    }

    if (Object.keys(errors).length > 0) {
      setFormError(errors);
    } else {
      actions.updateProfile({ first_name: firstName, last_name: lastName });
      setFormData({ first_name: firstName, last_name: lastName });
      setFormError({});
    }
  };

  const breadcrumbs = buildBreadcrumbs(t, "profileOverview");

  return (
    <div className="Page AccountPage">
      <div className="Page-content AccountPage-content">
        <PageHeader
          breadcrumbs={breadcrumbs}
          flash={profilePageMessage}
          title={t("profile.tabs.profil")}
          bannerClassName="AccountBanner"
        />

        <ProfileFormAny
          onChange={onFormChange}
          onSubmit={onFormSubmit}
          formData={formData}
          formError={formError}
          userData={userData}
          customerData={customerData}
          message={profilePageMessage}
          readonly={!!adminToken}
        />
      </div>
    </div>
  );
}

export default compose(withTranslation(), withAuthData)(ProfilePage);
